/**
 * Slide-in notification drawer.
 *
 * Triggered from the sidebar bell (desktop) or the header bell (mobile).
 * Loads notifications on open, exposes "Mark all read", deep-links each
 * row to its associated job.
 *
 * The chrome (backdrop, slide animation, close affordance, escape key)
 * lives in RightDrawer. This file is just the notification-specific
 * content + side effects.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { me } from '@/lib/api';
import type { Notification } from '@/lib/types';
import { classNames, formatRelativeDate } from '@/lib/format';
import { Empty, ErrorMessage, LoadingBlock } from './States';
import { RightDrawer } from './RightDrawer';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called after Mark all read so the parent can refresh its unread count. */
  onUnreadChange: () => void;
};

export function NotificationDrawer({ open, onClose, onUnreadChange }: Props) {
  const [items, setItems] = useState<Notification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Load whenever the drawer opens. Cheap, fresh, no polling needed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    me.notifications()
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load notifications');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const markAllRead = async () => {
    try {
      await me.readAll();
      // Optimistic local update; keeps the panel useful without a refetch.
      setItems((prev) => prev?.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })) ?? null);
      onUnreadChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mark all read failed');
    }
  };

  const onItemClick = (n: Notification) => {
    const link = typeof n.payload.link === 'string' ? n.payload.link : null;
    onClose();
    if (link) navigate(link);
    else if (n.jobId) navigate(`/jobs/${n.jobId}`);
  };

  const footer =
    items && items.length > 0 ? (
      <button
        onClick={markAllRead}
        className="kg-btn-ghost text-xs"
        disabled={items.every((n) => !!n.readAt)}
      >
        Mark all read
      </button>
    ) : null;

  return (
    <RightDrawer open={open} onClose={onClose} title="Notifications" footer={footer}>
      {error && (
        <div className="p-4">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}
      {!error && !items && (
        <div className="p-6">
          <LoadingBlock lines={5} />
        </div>
      )}
      {!error && items && items.length === 0 && (
        <div className="p-10 text-center">
          <p className="font-serif text-xl text-kg-grey-700 dark:text-kg-grey-200">
            All caught up
          </p>
          <p className="text-sm text-kg-grey-600 dark:text-kg-grey-400 mt-2">
            You'll see new activity here as it lands.
          </p>
        </div>
      )}
      {!error && items && items.length > 0 && (
        <ul className="kg-rows">
          {items.map((n) => {
            const title =
              typeof n.payload.title === 'string'
                ? n.payload.title
                : n.type.replace(/_/g, ' ').toLowerCase();
            const body =
              typeof n.payload.body === 'string' ? n.payload.body : null;
            const isUnread = !n.readAt;
            return (
              <li key={n.id}>
                <button
                  onClick={() => onItemClick(n)}
                  className={classNames(
                    'w-full text-left px-6 py-4 hover:bg-kg-grey-50 dark:hover:bg-kg-grey-800 transition-colors',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-kg-black',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className={classNames(
                        'mt-1 w-2 h-2 rounded-full shrink-0',
                        isUnread ? 'bg-red-700' : 'bg-transparent',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={classNames(
                          'text-sm',
                          isUnread
                            ? 'text-kg-black dark:text-white font-medium'
                            : 'text-kg-grey-700 dark:text-kg-grey-300',
                        )}
                      >
                        {title}
                      </p>
                      {body && (
                        <p className="text-xs text-kg-grey-600 dark:text-kg-grey-400 mt-1 line-clamp-2">
                          {body}
                        </p>
                      )}
                      <p className="text-xs text-kg-grey-500 dark:text-kg-grey-400 mt-1">
                        {formatRelativeDate(n.createdAt) ?? <Empty />}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </RightDrawer>
  );
}
